import React from "react";
import {
  Drawer,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import LogoutIcon from "@mui/icons-material/Logout";

import { RetryImage } from "../RetryImage";

const EXPANDED_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

export interface SidebarNavItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  matchPath?: string;
}

interface SidebarProps {
  navItems: SidebarNavItem[];
  mobileOpen: boolean;
  collapsed: boolean;
  onToggleMobile: () => void;
  onToggleCollapse: () => void;
  onLogout: () => void;
  companyLogo?: string | null;
  companyName?: string;
  userName?: string;
  userInitials?: string;
}

export const Sidebar: React.FC<SidebarProps> = ({
  navItems,
  mobileOpen,
  collapsed,
  onToggleMobile,
  onToggleCollapse,
  onLogout,
  companyLogo,
  companyName,
  userName,
  userInitials,
}) => {
  const location = useLocation();
  const drawerWidth = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;

  const isActive = (item: SidebarNavItem) => {
    if (item.matchPath) {
      return location.pathname.includes(item.matchPath);
    }
    return location.pathname === item.path;
  };

  const drawerContent = (isMobile: boolean) => {
    const width = isMobile ? EXPANDED_WIDTH : drawerWidth;
    const showText = isMobile || !collapsed;

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          width,
          overflow: "hidden",
        }}
      >
        {/* Logo / Company */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: showText ? 2.5 : 0,
            py: 2.5,
            justifyContent: showText ? "flex-start" : "center",
            minHeight: 80,
          }}
        >
          <RetryImage
            src={
              typeof companyLogo === "string"
                ? `${process.env.REACT_APP_COMPANY_LOGO_URL}/${companyLogo}`
                : undefined
            }
            sx={{
              width: 40,
              height: 40,
              bgcolor: "secondary.main",
              flexShrink: 0,
            }}
          />
          {showText && (
            <Typography
              variant="subtitle1"
              sx={{
                color: "#FFFFFF",
                fontWeight: 700,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {companyName || "BigFoot"}
            </Typography>
          )}
        </Box>

        {/* Navigation */}
        <List sx={{ flex: 1, px: 1, py: 0.5 }}>
          {navItems.map((item) => {
            const active = isActive(item);
            return (
              <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                <Tooltip
                  title={!showText ? item.label : ""}
                  placement="right"
                  arrow
                >
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    onClick={isMobile ? onToggleMobile : undefined}
                    sx={{
                      borderRadius: 2,
                      minHeight: 44,
                      px: showText ? 2 : 0,
                      justifyContent: showText ? "flex-start" : "center",
                      position: "relative",
                      bgcolor: active
                        ? "rgba(0, 180, 216, 0.12)"
                        : "transparent",
                      "&:hover": {
                        bgcolor: active
                          ? "rgba(0, 180, 216, 0.18)"
                          : "rgba(255, 255, 255, 0.06)",
                      },
                      "&::before": active
                        ? {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 6,
                            bottom: 6,
                            width: 3,
                            borderRadius: 4,
                            bgcolor: "secondary.main",
                          }
                        : {},
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: showText ? 40 : "unset",
                        justifyContent: "center",
                        color: active ? "secondary.main" : "rgba(226, 232, 240, 0.7)",
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    {showText && (
                      <ListItemText
                        primary={item.label}
                        primaryTypographyProps={{
                          fontSize: "0.875rem",
                          fontWeight: active ? 600 : 400,
                          color: active ? "#FFFFFF" : "rgba(226, 232, 240, 0.85)",
                        }}
                      />
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        {/* Bottom: User info + collapse */}
        <Box sx={{ px: showText ? 2 : 1, pb: 2, pt: 1 }}>
          {/* User info */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{
              px: showText ? 1 : 0,
              py: 1,
              justifyContent: showText ? "flex-start" : "center",
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: "secondary.main",
                fontSize: "0.8rem",
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {userInitials || "?"}
            </Avatar>
            {showText && (
              <Typography
                variant="body2"
                sx={{
                  color: "rgba(226, 232, 240, 0.85)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  flex: 1,
                }}
              >
                {userName || "User"}
              </Typography>
            )}
            {showText && (
              <Tooltip title="Logout" placement="right">
                <IconButton
                  onClick={onLogout}
                  size="small"
                  sx={{ color: "rgba(226, 232, 240, 0.6)", "&:hover": { color: "#EF4444" } }}
                >
                  <LogoutIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>

          {/* Collapse toggle - desktop only */}
          {!isMobile && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <IconButton
                onClick={onToggleCollapse}
                size="small"
                sx={{
                  color: "rgba(226, 232, 240, 0.5)",
                  "&:hover": { color: "#FFFFFF", bgcolor: "rgba(255,255,255,0.08)" },
                }}
              >
                {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Box
      component="nav"
      sx={{
        width: { md: drawerWidth },
        flexShrink: { md: 0 },
        transition: "width 0.2s ease",
      }}
    >
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onToggleMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: EXPANDED_WIDTH,
          },
        }}
      >
        {drawerContent(true)}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", md: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            transition: "width 0.2s ease",
            overflowX: "hidden",
          },
        }}
        open
      >
        {drawerContent(false)}
      </Drawer>
    </Box>
  );
};
